import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getNotifications,
  getUnreadCount,
  markAsRead as apiMarkAsRead,
  markAllAsRead as apiMarkAllAsRead,
  type Notification
} from 'api/notification';

const POLL_INTERVAL_MS = 30_000;

export default function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      // Backend: { unreadCount: N } → axios: { data: { unreadCount: N } }
      const count = res?.data?.unreadCount ?? 0;
      setUnreadCount(count);
    } catch (err) {
      console.error('[Notifications] Failed to fetch unread count:', err);
    }
  }, []);

  const fetchNotifications = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await getNotifications(pageNum, 20);
      // Backend: { data: [...], pagination: {...} } → axios: { data: { data: [...] } }
      const items = res?.data?.data ?? [];

      if (pageNum === 1) {
        setNotifications(items);
      } else {
        setNotifications((prev) => [...prev, ...items]);
      }

      setPage(pageNum);
      setHasMore(items.length === 20);
    } catch (err) {
      console.error('[Notifications] Failed to fetch notifications:', err);
      if (pageNum === 1) setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(
    async (id: string) => {
      // Optimistic update first
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        await apiMarkAsRead(id);
      } catch (err) {
        console.error(`[Notifications] Failed to mark ${id} as read:`, err);
        // Re-sync from server since optimistic update may be wrong
        await fetchUnreadCount();
      }
    },
    [fetchUnreadCount]
  );

  const markAllRead = useCallback(async () => {
    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
    setUnreadCount(0);

    try {
      await apiMarkAllAsRead();
    } catch (err) {
      console.error('[Notifications] Failed to mark all as read:', err);
      // Re-sync from server
      await fetchUnreadCount();
    }
  }, [fetchUnreadCount]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchNotifications(page + 1);
    }
  }, [loading, hasMore, page, fetchNotifications]);

  // Initial fetch + polling
  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchUnreadCount]);

  // Re-fetch on window focus
  useEffect(() => {
    const handler = () => fetchUnreadCount();
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    hasMore,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllRead,
    loadMore
  };
}
